import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiFileText, FiCheckCircle, FiAlertCircle, FiInfo} = FiIcons;

const AGBPage = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#3466ad] to-[#2a5291] text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-black/10 to-black/20"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
          >
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiFileText} className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Allgemeine Geschäftsbedingungen (AGB)
            </h1>
            <p className="text-xl opacity-90">
              Transparente Vertragsbedingungen für unsere Bauleistungen
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="space-y-12"
          >
            {/* Company Information Box */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm border-l-4 border-[#3466ad]">
              <div className="text-lg text-gray-700 space-y-1">
                <p className="font-semibold text-gray-900">RUDT Bau – Valentina Schneider</p>
                <p>Wassenbergerstr. 41</p>
                <p>52525 Heinsberg</p>
              </div>
            </div>

            {/* § 1 Geltungsbereich */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-[#3466ad] rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    § 1 Geltungsbereich
                  </h2>
                </div>
              </div>

              <div className="space-y-4 text-gray-700">
                <div className="pl-16">
                  <ol className="space-y-3 list-decimal list-inside">
                    <li className="leading-relaxed">
                      Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge über Bauleistungen, insbesondere Rohbauarbeiten, zwischen <strong>RUDT Bau – Valentina Schneider</strong> (nachfolgend „Auftragnehmer") und dem jeweiligen Auftraggeber (nachfolgend „Kunde").
                    </li>
                    <li className="leading-relaxed">
                      Abweichende, entgegenstehende oder ergänzende Bedingungen des Kunden werden nicht Vertragsbestandteil, es sei denn, der Auftragnehmer stimmt ihrer Geltung ausdrücklich in Textform (z. B. E-Mail) zu.
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            {/* § 2 Vertragsgrundlage / Einbeziehung der VOB/B */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 2 Vertragsgrundlage / Einbeziehung der VOB/B
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-4 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Vertragsgrundlage sind das jeweilige Angebot bzw. der Bauvertrag, diese AGB sowie ergänzend die gesetzlichen Vorschriften (insbesondere BGB-Werkvertragsrecht), soweit nachfolgend nichts anderes geregelt ist.
                  </li>
                  <li className="leading-relaxed">
                    <strong>VOB/B (Version):</strong> Sofern die Parteien dies <strong>ausdrücklich</strong> vereinbaren, wird die <strong>VOB/B (Vergabe- und Vertragsordnung für Bauleistungen – Teil B) in der bei Vertragsschluss gültigen Fassung</strong> Bestandteil des Vertrages.
                    <ul className="mt-2 ml-6 space-y-2 list-disc">
                      <li>Die VOB/B wird dem Kunden vor Vertragsschluss zur Verfügung gestellt oder ist dem Kunden nachweislich bereits bekannt.</li>
                      <li>Bei Verträgen mit <strong>Verbrauchern</strong> gilt die VOB/B nur, wenn sie vollständig übergeben wurde und die Einbeziehung ausdrücklich in Textform vereinbart ist.</li>
                    </ul>
                  </li>
                  <li className="leading-relaxed">
                    Im Falle von Widersprüchen zwischen dem Vertrag/Angebot und der VOB/B haben die individuell vereinbarten Regelungen Vorrang. Im Übrigen gelten diese AGB ergänzend.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 3 Angebote und Vertragsschluss */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 3 Angebote und Vertragsschluss
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Angebote des Auftragnehmers sind freibleibend und unverbindlich, sofern sie nicht ausdrücklich als verbindlich bezeichnet sind.
                  </li>
                  <li className="leading-relaxed">
                    Ein Vertrag kommt durch schriftliche Auftragsbestätigung, Unterzeichnung eines Bauvertrages oder durch tatsächlichen Beginn der Arbeiten zustande.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 4 Leistungsumfang / Leistungsänderungen */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 4 Leistungsumfang / Leistungsänderungen
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Der Umfang der Leistungen ergibt sich ausschließlich aus dem schriftlichen Angebot bzw. der Leistungsbeschreibung.
                  </li>
                  <li className="leading-relaxed">
                    Änderungen, Ergänzungen oder Zusatzleistungen bedürfen einer Vereinbarung in Textform. Sie sind gesondert zu vergüten.
                  </li>
                  <li className="leading-relaxed">
                    Werden während der Ausführung Leistungen erforderlich, die zur ordnungsgemäßen Herstellung oder zur Betriebssicherheit notwendig sind und bei Angebotsabgabe nicht vorhersehbar waren, informiert der Auftragnehmer den Kunden unverzüglich. Soweit eine sofortige Entscheidung erforderlich ist, um Schaden zu vermeiden oder den Bauablauf zu sichern, dürfen erforderliche Maßnahmen veranlasst werden; die Vergütung erfolgt nach den gesetzlichen Vorschriften bzw. nach Vereinbarung.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 5 Preise, Abschläge und Zahlungsbedingungen */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 5 Preise, Abschläge und Zahlungsbedingungen
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Alle Preise verstehen sich netto zuzüglich der gesetzlichen Mehrwertsteuer, sofern nicht ausdrücklich als Bruttopreis ausgewiesen.
                  </li>
                  <li className="leading-relaxed">
                    Abschlagszahlungen können entsprechend dem Baufortschritt vereinbart und abgerechnet werden.
                  </li>
                  <li className="leading-relaxed">
                    Rechnungen sind, sofern nicht anders vereinbart, innerhalb von <strong>14 Tagen</strong> ab Rechnungsdatum ohne Abzug zahlbar.
                  </li>
                  <li className="leading-relaxed">
                    Bei Zahlungsverzug ist der Auftragnehmer berechtigt, Verzugszinsen in gesetzlicher Höhe zu berechnen. Zudem kann der Auftragnehmer nach Mahnung die Arbeiten bis zum Ausgleich fälliger Beträge einstellen, soweit dies rechtlich zulässig ist.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 6 Ausführungsfristen / Behinderungen */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 6 Ausführungsfristen / Behinderungen
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Ausführungsfristen beginnen erst, wenn alle technischen und kaufmännischen Fragen geklärt sind und der Kunde seine Mitwirkungspflichten erfüllt hat.
                  </li>
                  <li className="leading-relaxed">
                    Verzögerungen durch höhere Gewalt, Witterungseinflüsse, behördliche Maßnahmen, Streik, Lieferengpässe oder andere unvorhersehbare Ereignisse, die der Auftragnehmer nicht zu vertreten hat, verlängern die Fristen angemessen. Dies gilt entsprechend bei Behinderungen i. S. d. VOB/B, sofern diese wirksam vereinbart ist.
                  </li>
                  <li className="leading-relaxed">
                    Schadensersatzansprüche wegen Verzögerung sind ausgeschlossen, sofern kein vorsätzliches oder grob fahrlässiges Verhalten vorliegt.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 7 Mitwirkungspflichten des Kunden */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-[#3466ad] rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiInfo} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    § 7 Mitwirkungspflichten des Kunden
                  </h2>
                </div>
              </div>

              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">Der Kunde hat insbesondere:</p>
                <ul className="space-y-2 ml-6 list-disc">
                  <li>einen ungehinderten Baustellenzugang zu gewährleisten,</li>
                  <li>notwendige Genehmigungen rechtzeitig bereitzustellen,</li>
                  <li>Strom und Wasser in erforderlichem Umfang kostenfrei zur Verfügung zu stellen (sofern nicht anders vereinbart),</li>
                  <li>Vorleistungen anderer Gewerke rechtzeitig und fachgerecht fertigzustellen,</li>
                  <li>Pläne, Statiken und sonstige Unterlagen vollständig und rechtzeitig zu übergeben.</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  Kommt der Kunde diesen Pflichten nicht nach, verlängern sich Fristen angemessen; hierdurch entstehende Mehrkosten (z. B. Stillstand, Umsetzungen, zusätzliche Fahrten, Lagerkosten) sind vom Kunden zu tragen.
                </p>
              </div>
            </div>

            {/* § 8 Abnahme */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 8 Abnahme
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Nach Fertigstellung der Arbeiten wird eine Abnahme gemeinsam durchgeführt, sofern gesetzlich oder vertraglich vorgesehen.
                  </li>
                  <li className="leading-relaxed">
                    Wegen unwesentlicher Mängel darf die Abnahme nicht verweigert werden.
                  </li>
                  <li className="leading-relaxed">
                    Sofern der Kunde die Abnahme ohne nachvollziehbare Begründung verweigert oder die Leistung nutzt, kann eine Abnahme nach den gesetzlichen Vorschriften bzw. nach VOB/B (falls vereinbart) eintreten.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 9 Gewährleistung / Mängelrechte */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 9 Gewährleistung / Mängelrechte
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Es gelten die gesetzlichen Gewährleistungsregelungen; bei wirksam vereinbarter VOB/B gelten deren Mängelregelungen ergänzend.
                  </li>
                  <li className="leading-relaxed">
                    Der Auftragnehmer hat das Recht zur Nacherfüllung (Nachbesserung oder Neuherstellung) innerhalb angemessener Frist.
                  </li>
                  <li className="leading-relaxed">
                    Eigenmächtige Mängelbeseitigungen durch den Kunden schließen Ansprüche aus, soweit der Kunde dem Auftragnehmer nicht zuvor eine angemessene Frist zur Nacherfüllung gesetzt hat, es sei denn, ein sofortiges Handeln war zur Gefahrenabwehr zwingend erforderlich.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 10 Haftung */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-[#3466ad] rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    § 10 Haftung
                  </h2>
                </div>
              </div>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Der Auftragnehmer haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit.
                  </li>
                  <li className="leading-relaxed">
                    Bei einfacher Fahrlässigkeit haftet der Auftragnehmer nur bei Verletzung wesentlicher Vertragspflichten (Kardinalpflichten) und begrenzt auf den vertragstypischen, vorhersehbaren Schaden.
                  </li>
                  <li className="leading-relaxed">
                    Eine Haftung für mittelbare Schäden und Folgeschäden (z. B. Nutzungsausfall, entgangener Gewinn) ist ausgeschlossen, soweit gesetzlich zulässig.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 11 Eigentumsvorbehalt */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 11 Eigentumsvorbehalt
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Bis zur vollständigen Zahlung bleiben gelieferte Baustoffe und Materialien Eigentum des Auftragnehmers, soweit dies rechtlich zulässig ist.
              </p>
            </div>

            {/* § 12 Kündigung */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 12 Kündigung
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Beide Parteien können den Vertrag nach den gesetzlichen Vorschriften kündigen; bei wirksam vereinbarter VOB/B gelten deren Kündigungsregelungen ergänzend.
                  </li>
                  <li className="leading-relaxed">
                    Im Falle einer freien Kündigung durch den Kunden hat der Auftragnehmer Anspruch auf Vergütung der bis zur Kündigung erbrachten Leistungen sowie auf Ersatz entgangenen Gewinns nach Maßgabe des § 648 BGB, soweit anwendbar.
                  </li>
                </ol>
              </div>
            </div>

            {/* § 13 Gerichtsstand und anwendbares Recht */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 13 Gerichtsstand und anwendbares Recht
              </h2>

              <div className="space-y-4 text-gray-700">
                <ol className="space-y-3 list-decimal list-inside">
                  <li className="leading-relaxed">
                    Es gilt das Recht der Bundesrepublik Deutschland.
                  </li>
                  <li className="leading-relaxed">
                    Gerichtsstand ist – soweit gesetzlich zulässig – der Sitz des Auftragnehmers (Heinsberg).
                  </li>
                </ol>
              </div>
            </div>

            {/* § 14 Salvatorische Klausel */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 14 Salvatorische Klausel
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Sollte eine Bestimmung dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
              </p>
            </div>

            {/* § 15 Datenschutz */}
            <div className="bg-blue-50 border-l-4 border-[#3466ad] rounded-r-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                § 15 Datenschutz
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Personenbezogene Daten werden ausschließlich im Rahmen der gesetzlichen Datenschutzvorschriften verarbeitet. Weitere Informationen sind der Datenschutzerklärung des Unternehmens zu entnehmen.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AGBPage;